import {store} from '../../index';
import {RectUtil} from '../../utils/RectUtil';
import {updateCustomCursorStyle, updateInlineLabelPicker} from '../../store/general/actionCreators';
import {CustomCursorStyle} from '../../data/enums/CustomCursorStyle';
import {EditorData} from '../../data/EditorData';
import {BaseRenderEngine} from './BaseRenderEngine';
import {RenderEngineSettings} from '../../settings/RenderEngineSettings';
import {IPoint} from '../../interfaces/IPoint';
import {ILine} from '../../interfaces/ILine';
import {DrawUtil} from '../../utils/DrawUtil';
import {IRect} from '../../interfaces/IRect';
import {ImageData, LabelPolygon} from '../../store/labels/types';
import {LabelsSelector} from '../../store/selectors/LabelsSelector';
import {
    updateActiveLabelId,
    updateActiveLabelNameId,
    updateFirstLabelCreatedFlag,
    updateHighlightedLabelId,
    updateImageDataById
} from '../../store/labels/actionCreators';
import {LineUtil} from '../../utils/LineUtil';
import {MouseEventUtil} from '../../utils/MouseEventUtil';
import {EventType} from '../../data/enums/EventType';
import {RenderEngineUtil} from '../../utils/RenderEngineUtil';
import {LabelType} from '../../data/enums/LabelType';
import {EditorActions} from '../actions/EditorActions';
import {GeneralSelector} from '../../store/selectors/GeneralSelector';
import {Settings} from '../../settings/Settings';
import {LabelUtil} from '../../utils/LabelUtil';

export class PolygonRenderEngine extends BaseRenderEngine {

    // =================================================================================================================
    // STATE
    // =================================================================================================================

    private activePath: IPoint[] = [];
    private resizeAnchorIndex: number = null;
    private suggestedAnchorPositionOnCanvas: IPoint = null;
    private suggestedAnchorIndexInPolygon: number = null;
    
    public constructor(canvas: HTMLCanvasElement) {
        super(canvas);
        this.labelType = LabelType.POLYGON;
    }

    // =================================================================================================================
    // EVENT HANDLERS
    // =================================================================================================================

    public update(data: EditorData): void {
        if (!!data.event) {
            switch (MouseEventUtil.getEventType(data.event)) {
                case EventType.MOUSE_MOVE:
                    this.mouseMoveHandler(data);
                    break;
                case EventType.MOUSE_UP:
                    this.mouseUpHandler(data);
                    break;
                case EventType.MOUSE_DOWN:
                    this.mouseDownHandler(data);
                    break;
                default:
                    break;
            }
        }
    }

    public mouseDownHandler(data: EditorData): void {
        const isMouseOverCanvas: boolean = RenderEngineUtil.isMouseOverCanvas(data);
        if (isMouseOverCanvas) {
            if (this.isCreationInProgress()) {
                const polygonUnderMouse: LabelPolygon = this.getPolygonUnderMouse(data);
                const startAnchorOnCanvas: IPoint = RenderEngineUtil.transferPointFromImageToViewPortContent(this.activePath[0], data);
                const isMouseOverStartAnchor: boolean = RenderEngineUtil.isMouseOverAnchor(
                    data.mousePositionOnViewPortContent, startAnchorOnCanvas, RenderEngineSettings.anchorSize);
                if (isMouseOverStartAnchor && !polygonUnderMouse) {
                    this.addLabelAndFinishCreation(data);
                } else  {
                    this.updateActivelyCreatedLabel(data);
                }
            } else {
                const polygonUnderMouse: LabelPolygon = this.getPolygonUnderMouse(data);
                if (!!polygonUnderMouse) {
                    const anchorIndex: number = polygonUnderMouse.vertices.reduce(
                        (indexUnderMouse: number, anchor: IPoint, index: number) => {
                        if (indexUnderMouse === null) {
                            const anchorOnCanvas: IPoint = RenderEngineUtil.transferPointFromImageToViewPortContent(anchor, data);
                            if (this.isMouseOverAnchor(data.mousePositionOnViewPortContent, anchorOnCanvas)) {
                                return index;
                            }
                        }
                        return indexUnderMouse;
                    }, null);

                    if (anchorIndex !== null) {
                        this.startExistingLabelResize(data, polygonUnderMouse.id, anchorIndex);
                    } else {
                        store.dispatch(updateActiveLabelId(polygonUnderMouse.id));
                        const isMouseOverNewAnchor: boolean = this.isMouseOverAnchor(data.mousePositionOnViewPortContent, this.suggestedAnchorPositionOnCanvas);
                        if (isMouseOverNewAnchor) {
                            this.addSuggestedAnchorToPolygonLabel(data);
                        }
                    }
                } else {
                    this.updateActivelyCreatedLabel(data);
                }
            }
        }
    }

    public mouseUpHandler(data: EditorData): void {
        if (this.isResizeInProgress())
            this.endExistingLabelResize(data);
    }

    public mouseMoveHandler(data: EditorData): void {
        if (!!data.viewPortContentImageRect && !!data.mousePositionOnViewPortContent) {
            const isOverImage: boolean = RenderEngineUtil.isMouseOverImage(data);
            if (isOverImage && !this.isCreationInProgress()) {
                const labelPolygon: LabelPolygon = this.getPolygonUnderMouse(data);
                if (!!labelPolygon && !this.isResizeInProgress()) {
                    if (LabelsSelector.getHighlightedLabelId() !== labelPolygon.id) {
                        store.dispatch(updateHighlightedLabelId(labelPolygon.id))
                    }
                    const pathOnCanvas: IPoint[] = RenderEngineUtil.transferPolygonFromImageToViewPortContent(labelPolygon.vertices, data);
                    const linesOnCanvas: ILine[] = this.mapPointsToLines(pathOnCanvas.concat(pathOnCanvas[0]));

                    for (let j = 0; j < linesOnCanvas.length; j++) {
                        const mouseOverLine = RenderEngineUtil.isMouseOverLine(
                            data.mousePositionOnViewPortContent,
                            linesOnCanvas[j],
                            RenderEngineSettings.anchorHoverSize.width / 2
                        )
                        if (mouseOverLine) {
                            this.suggestedAnchorPositionOnCanvas = LineUtil.getCenter(linesOnCanvas[j]);
                            this.suggestedAnchorIndexInPolygon = j + 1;
                            break;
                        }
                    }
                } else {
                    if (LabelsSelector.getHighlightedLabelId() !== null) {
                        store.dispatch(updateHighlightedLabelId(null));
                        this.discardSuggestedPoint();
                    }
                }
            }
        }
    }
    public pasteHandler(): void {
            const copiedLabels = this.getCopiedLabels<LabelPolygon>();
            if (copiedLabels.length === 0) return;
            copiedLabels.forEach((polyLabel) => {
                store.dispatch(updateActiveLabelNameId(polyLabel.labelId));
                this.addPolygonLabel(polyLabel.vertices);
            });
            EditorActions.fullRender();
    }

    // =================================================================================================================
    // RENDERING
    // =================================================================================================================

    public render(data: EditorData): void {
        const imageData: ImageData = LabelsSelector.getActiveImageData();
        if (imageData) {
            this.drawExistingLabels(data);
            this.drawActivelyCreatedLabel(data);
            this.drawActivelyResizeLabel(data);
            this.updateCursorStyle(data);
            this.drawSuggestedAnchor(data);
            this.drawAILabels(data);
        }
    }

    private updateCursorStyle(data: EditorData) {
        if (!!this.canvas && !!data.mousePositionOnViewPortContent && !GeneralSelector.getImageDragModeStatus()) {
            const isMouseOverCanvas: boolean = RenderEngineUtil.isMouseOverCanvas(data);
            if (isMouseOverCanvas) {
                if (this.isCreationInProgress()) {
                    const startAnchorOnCanvas: IPoint = RenderEngineUtil.transferPointFromImageToViewPortContent(this.activePath[0], data);
                    const isMouseOverStartAnchor: boolean = this.isMouseOverAnchor(data.mousePositionOnViewPortContent, startAnchorOnCanvas);
                    if (isMouseOverStartAnchor && this.activePath.length > 2)
                        store.dispatch(updateCustomCursorStyle(CustomCursorStyle.CLOSE));
                    else
                        store.dispatch(updateCustomCursorStyle(CustomCursorStyle.DEFAULT));
                } else {
                    const anchorUnderMouse: IPoint = this.getAnchorUnderMouse(data);
                    const isMouseOverNewAnchor: boolean = this.isMouseOverAnchor(data.mousePositionOnViewPortContent, this.suggestedAnchorPositionOnCanvas);
                    if (!!isMouseOverNewAnchor) {
                        store.dispatch(updateCustomCursorStyle(CustomCursorStyle.ADD));
                    } else if (this.isResizeInProgress()) {
                        store.dispatch(updateCustomCursorStyle(CustomCursorStyle.MOVE));
                    } else if (!!anchorUnderMouse) {
                        store.dispatch(updateCustomCursorStyle(CustomCursorStyle.MOVE));
                    } else {
                        RenderEngineUtil.wrapDefaultCursorStyleInCancel(data);
                    }
                }
                this.canvas.style.cursor = 'none';
            } else {
                this.canvas.style.cursor = 'default';
            }
        }
    }

    private drawActivelyCreatedLabel(data: EditorData) {
        const pathOnCanvas: IPoint[] = RenderEngineUtil.transferPolygonFromImageToViewPortContent(this.activePath, data)
            .map((point: IPoint) => RenderEngineUtil.setPointBetweenPixels(point));
        const path = pathOnCanvas.concat(data.mousePositionOnViewPortContent);
        const lines: ILine[] = this.mapPointsToLines(path);
        const lineColor: string = BaseRenderEngine.resolveLabelLineColor(null, true)
        const anchorColor: string = BaseRenderEngine.resolveLabelAnchorColor(true)
        const opacityValue = localStorage.getItem('opacityValue');
        const opacity = opacityValue ? parseFloat(opacityValue) : 0.2;
        DrawUtil.drawPolygonWithFill(this.canvas, path, DrawUtil.hexToRGB(lineColor, opacity - 0.2));
        lines.forEach((line: ILine) => {
            DrawUtil.drawLine(this.canvas, line.start, line.end, lineColor, RenderEngineSettings.LINE_THICKNESS);
        });
        pathOnCanvas.forEach((point: IPoint) => {
            DrawUtil.drawCircleWithFill(this.canvas, point, Settings.RESIZE_HANDLE_DIMENSION_PX/2, anchorColor);
        })
    }

    private drawActivelyResizeLabel(data: EditorData) {
        const activeLabelPolygon: LabelPolygon = LabelsSelector.getActivePolygonLabel();
        if (!!activeLabelPolygon && this.isResizeInProgress()) {
            const snappedMousePosition: IPoint = RectUtil.snapPointToRect(data.mousePositionOnViewPortContent, data.viewPortContentImageRect);
            const polygonOnCanvas: IPoint[] = activeLabelPolygon.vertices.map((point: IPoint, index: number) => {
                return index === this.resizeAnchorIndex ? snappedMousePosition : RenderEngineUtil.transferPointFromImageToViewPortContent(point, data);
            });
            this.drawPolygon(activeLabelPolygon.labelId, polygonOnCanvas, true);
        }
    }

    // private drawExistingLabels(data: EditorData) {
    //     const activeLabelId: string = LabelsSelector.getActiveLabelId();
    //     const highlightedLabelId: string = LabelsSelector.getHighlightedLabelId();
    //     const imageData: ImageData = LabelsSelector.getActiveImageData();
    //     imageData.labelPolygons.forEach((labelPolygon: LabelPolygon) => {
    //         if(labelPolygon.show    ){
    //         const isActive: boolean = labelPolygon.id === activeLabelId || labelPolygon.id === highlightedLabelId;
    //         const pathOnCanvas: IPoint[] = RenderEngineUtil.transferPolygonFromImageToViewPortContent(labelPolygon.vertices, data);
    //             if (!(labelPolygon.id == activeLabelId && this.isResizeInProgress())) {
    //                 this.drawPolygon(labelPolygon?.labelId, pathOnCanvas, isActive);
    //                 }
    //             }
           
    //     });
    // }


    private drawExistingLabels(data: EditorData) {
        const activeLabelId: string = LabelsSelector.getActiveLabelId();
        const highlightedLabelId: string = LabelsSelector.getHighlightedLabelId();
        const imageData: ImageData = LabelsSelector.getActiveImageData();
        
        // Retrieve opacity value from localStorage and set a default
        const opacityValue = localStorage.getItem('opacityValue');
        const opacity = opacityValue ? parseFloat(opacityValue) : 0.5;
    
        imageData.labelPolygons.forEach((labelPolygon: LabelPolygon) => {
            if (labelPolygon.show !== false) {
                const isActive: boolean = labelPolygon.id === activeLabelId || labelPolygon.id === highlightedLabelId;
                const pathOnCanvas: IPoint[] = RenderEngineUtil.transferPolygonFromImageToViewPortContent(labelPolygon.vertices, data);
    
                if (!(labelPolygon.id === activeLabelId && this.isResizeInProgress())) {
                    const lineColor: string = BaseRenderEngine.resolveLabelLineColor(labelPolygon?.labelId, isActive);
                    const filledColor = DrawUtil.hexToRGB(lineColor, opacity);
                    
                    // Draw the polygon with fill and opacity
                    DrawUtil.drawPolygonWithFill(this.canvas, pathOnCanvas, filledColor);
                    
                    // Call drawPolygon with the modified color and opacity
                    this.drawPolygon(labelPolygon?.labelId, pathOnCanvas, isActive);
                }
            }
        });
    }
    
    private drawAILabels(data: EditorData) {
        // let test
        // var imageData: ImageData = LabelsSelector.getActiveImageData();
        // test=imageData
        let test = JSON.parse(localStorage.getItem("aipreditions"))
       var labelPolygons=[
            {
              "id": "27f55b20-33b3-40fa-84b4-ba3b6de52fc5",
              "labelId": "0",
              "vertices": [
                {
                  "x": 59.69773299748111,
                  "y": 225.3148614609572
                },
                {
                  "x": 68.76574307304786,
                  "y": 211.71284634760707
                },
                {
                  "x": 114.86146095717885,
                  "y": 210.95717884130985
                },
                {
                  "x": 125.44080604534005,
                  "y": 228.3375314861461
                },
                {
                  "x": 125.44080604534005,
                  "y": 246.47355163727963
                },
                {
                  "x": 125.44080604534005,
                  "y": 266.8765743073048
                },
                {
                  "x": 129.97481108312343,
                  "y": 296.34760705289676
                },
                {
                  "x": 124.68513853904282,
                  "y": 304.65994962216627
                },
                {
                  "x": 116.3727959697733,
                  "y": 343.95465994962217
                },
                {
                  "x": 105.79345088161209,
                  "y": 353.0226700251889
                },
                {
                  "x": 63.476070528967256,
                  "y": 352.26700251889173
                },
                {
                  "x": 55.16372795969773,
                  "y": 342.44332493702774
                },
                {
                  "x": 57.430730478589425,
                  "y": 276.7002518891688
                }
              ],
              "isCreatedByAI": true,
            }
          ];
        test?.labelPolygons?.forEach((labelPolygon: LabelPolygon) => {
            
            const pathOnCanvas: IPoint[] = RenderEngineUtil.transferPolygonFromImageToViewPortContent(labelPolygon.vertices, data);
         
                this.drawPolygondoted(labelPolygon.labelId, pathOnCanvas, false);
        });
    }


    private drawPolygon(labelId: string | null, polygon: IPoint[], isActive: boolean) {
        const lineColor: string = BaseRenderEngine.resolveLabelLineColor(labelId, true)
        const anchorColor: string = BaseRenderEngine.resolveLabelAnchorColor(true)
        const standardizedPoints: IPoint[] = polygon.map((point: IPoint) => RenderEngineUtil.setPointBetweenPixels(point));
        if (isActive) {
            const opacityValue = localStorage.getItem('opacityValue');
            const opacity = opacityValue ? parseFloat(opacityValue) : 0.2;
            DrawUtil.drawPolygonWithFill(this.canvas, standardizedPoints, DrawUtil.hexToRGB(lineColor, opacity - 0.2));
        }
        DrawUtil.drawPolygon(this.canvas, standardizedPoints, lineColor, RenderEngineSettings.LINE_THICKNESS);
        if (isActive) {
            standardizedPoints.forEach((point: IPoint) => {
                DrawUtil.drawCircleWithFill(this.canvas, point, Settings.RESIZE_HANDLE_DIMENSION_PX/2, anchorColor);
            })
        }
    }
    private drawPolygondoted(labelId: string | null, polygon: IPoint[], isActive: boolean) {
        const lineColor: string = BaseRenderEngine.resolveLabelLineColor(labelId, true)
        const anchorColor: string = BaseRenderEngine.resolveLabelAnchorColor(true)
        const standardizedPoints: IPoint[] = polygon.map((point: IPoint) => RenderEngineUtil.setPointBetweenPixels(point));
        // if (isActive) {
        //     DrawUtil.drawPolygonWithFill(this.canvas, standardizedPoints, DrawUtil.hexToRGB(lineColor, 0.2));
        // }
        DrawUtil.drawPolygondoted(this.canvas, standardizedPoints, lineColor, RenderEngineSettings.LINE_THICKNESS);
        // if (isActive) {
        //     standardizedPoints.forEach((point: IPoint) => {
        //         DrawUtil.drawCircleWithFill(this.canvas, point, Settings.RESIZE_HANDLE_DIMENSION_PX/2, anchorColor);
        //     })
        // }
    }


    private drawSuggestedAnchor(data: EditorData) {
        const anchorColor: string = BaseRenderEngine.resolveLabelAnchorColor(true)
        if (this.suggestedAnchorPositionOnCanvas) {
            const suggestedAnchorRect: IRect = RectUtil
                .getRectWithCenterAndSize(this.suggestedAnchorPositionOnCanvas, RenderEngineSettings.suggestedAnchorDetectionSize);
            const isMouseOverSuggestedAnchor: boolean = RectUtil.isPointInside(suggestedAnchorRect, data.mousePositionOnViewPortContent);

            if (isMouseOverSuggestedAnchor) {
                DrawUtil.drawCircleWithFill(
                    this.canvas, this.suggestedAnchorPositionOnCanvas, Settings.RESIZE_HANDLE_DIMENSION_PX/2, anchorColor);
            }
        }
    }

    // =================================================================================================================
    // CREATION
    // =================================================================================================================

    // private updateActivelyCreatedLabel(data: EditorData) {
    //     if (this.isCreationInProgress()) {
    //         const mousePositionSnapped: IPoint = RectUtil.snapPointToRect(data.mousePositionOnViewPortContent, data.viewPortContentImageRect);
    //         this.activePath.push(mousePositionSnapped);
    //          console.log('pointed', mousePositionSnapped);
    //     } else {
    //         const isMouseOverImage: boolean = RectUtil.isPointInside(data.viewPortContentImageRect, data.mousePositionOnViewPortContent);
    //         if (isMouseOverImage) {
    //             EditorActions.setViewPortActionsDisabledStatus(true);
    //             this.activePath.push(data.mousePositionOnViewPortContent);
    //             store.dispatch(updateActiveLabelId(null));
    //         }
    //     }
    // }


    private updateActivelyCreatedLabel(data: EditorData) {
        const storePoint = (pointOnImage: IPoint) => {
            let pointsArray: IPoint[] = JSON.parse(localStorage.getItem('mousePositionPoints')) || [];
            let undoIndex = JSON.parse(localStorage.getItem('undoindex')) || 0;
    
            if (undoIndex > 0) {
                pointsArray = pointsArray.slice(0, pointsArray.length - undoIndex);
                undoIndex = 0;
                localStorage.setItem('undoindex', JSON.stringify(undoIndex));
            }
   
    
            pointsArray.push(pointOnImage);
            localStorage.setItem('mousePositionPoints', JSON.stringify(pointsArray));
            this.activePath = pointsArray;
        };

        const toImagePoint = (pointOnCanvas: IPoint): IPoint =>
            RenderEngineUtil.transferPointFromViewPortContentToImage(pointOnCanvas, data);
    
        if (this.isCreationInProgress()) {
            const mousePositionSnapped: IPoint = RectUtil.snapPointToRect(
                data.mousePositionOnViewPortContent,
                data.viewPortContentImageRect
            );
            storePoint(toImagePoint(mousePositionSnapped));
        } else {
            const isMouseOverImage: boolean = RectUtil.isPointInside(
                data.viewPortContentImageRect,
                data.mousePositionOnViewPortContent
            );
    
            if (isMouseOverImage) {
                const pointOnImage = toImagePoint(data.mousePositionOnViewPortContent);
                this.activePath.push(pointOnImage);
                store.dispatch(updateActiveLabelId(null));
                storePoint(pointOnImage);
            }
        }
    }
    

public undo() {
    let storedPoints = JSON.parse(localStorage.getItem('mousePositionPoints')) || [];
    let undoindex = JSON.parse(localStorage.getItem('undoindex')) || 0;
    if (undoindex < storedPoints.length - 1) {
        undoindex++;
        localStorage.setItem('undoindex', JSON.stringify(undoindex));
        this.activePath = storedPoints.slice(0, storedPoints.length - undoindex);
    }
}

public redo() {
    let storedPoints = JSON.parse(localStorage.getItem('mousePositionPoints')) || [];
    let undoindex = JSON.parse(localStorage.getItem('undoindex')) || 0;
    if (undoindex > 0) {
        undoindex--;
        localStorage.setItem('undoindex', JSON.stringify(undoindex));
        this.activePath = storedPoints.slice(0, storedPoints.length - undoindex);
    }
}





    public cancelLabelCreation() {
        this.activePath = [];
        ['mousePositionPoints', 'undoindex'].forEach(key => localStorage.removeItem(key));
    }

    private finishLabelCreation() {
        this.activePath = [];
        ['mousePositionPoints', 'undoindex'].forEach(key => localStorage.removeItem(key));
    }

    public addLabelAndFinishCreation(data: EditorData) {
        if (this.isCreationInProgress() && this.activePath.length > 2) {
            // activePath is already stored in image coordinates so zoom mid-draw stays stable
            const polygonOnImage: IPoint[] = this.activePath;
            const shouldOpenInlinePicker = !LabelsSelector.getActiveLabelNameId();
            const created = this.addPolygonLabel(polygonOnImage);
            const ev = data.event as MouseEvent | undefined;
            if (created && shouldOpenInlinePicker) {
                // Polygon creation completes on mousedown, so defer opening the
                // picker until the event cycle settles to keep it visible.
                window.requestAnimationFrame(() => {
                    store.dispatch(updateInlineLabelPicker({
                        open: true,
                        clientX: ev?.clientX ?? null,
                        clientY: ev?.clientY ?? null,
                        shapeId: created.id,
                        shapeType: 'polygon',
                    }));
                });
            }
            this.finishLabelCreation();
        }
    }

    private addPolygonLabel(polygon: IPoint[]) {
        const activeLabelId = LabelsSelector.getActiveLabelNameId();
        const imageData: ImageData = LabelsSelector.getActiveImageData();
        const labelPolygon: LabelPolygon = LabelUtil.createLabelPolygon(activeLabelId, polygon);
        store.dispatch(updateImageDataById(imageData.image_id, {
            ...imageData,
            labelPolygons: [...imageData.labelPolygons, labelPolygon],
        }));
        store.dispatch(updateFirstLabelCreatedFlag(true));
        store.dispatch(updateActiveLabelId(labelPolygon.id));
        return labelPolygon;
    };

    // =================================================================================================================
    // TRANSFER
    // =================================================================================================================

    private startExistingLabelResize(data: EditorData, labelId: string, anchorIndex: number) {
        store.dispatch(updateActiveLabelId(labelId));
        this.resizeAnchorIndex = anchorIndex;
        EditorActions.setViewPortActionsDisabledStatus(true);
    }

    private endExistingLabelResize(data: EditorData) {
        this.applyResizeToPolygonLabel(data);
        this.resizeAnchorIndex = null;
        EditorActions.setViewPortActionsDisabledStatus(false);
    }

    private applyResizeToPolygonLabel(data: EditorData) {
        const imageData: ImageData = LabelsSelector.getActiveImageData();
        const activeLabel: LabelPolygon = LabelsSelector.getActivePolygonLabel();
        const newImageData = {
            ...imageData,
            labelPolygons: imageData.labelPolygons.map((polygon: LabelPolygon) => {
            if (polygon.id !== activeLabel.id) {
                return polygon
            } else {
                return {
                    ...polygon,
                    vertices: polygon.vertices.map((value: IPoint, index: number) => {
                        if (index !== this.resizeAnchorIndex) {
                            return value;
                        } else {
                            const snappedMousePosition: IPoint =
                                RectUtil.snapPointToRect(data.mousePositionOnViewPortContent, data.viewPortContentImageRect);
                            return RenderEngineUtil.transferPointFromViewPortContentToImage(snappedMousePosition, data);
                        }
                    })
                }
            }
            }),
        };
        store.dispatch(updateImageDataById(imageData.image_id, newImageData));
        store.dispatch(updateActiveLabelId(activeLabel.id));
    }

    private discardSuggestedPoint(): void {
        this.suggestedAnchorIndexInPolygon = null;
        this.suggestedAnchorPositionOnCanvas = null;
    }

    // =================================================================================================================
    // UPDATE
    // =================================================================================================================

    private addSuggestedAnchorToPolygonLabel(data: EditorData) {
        const imageData: ImageData = LabelsSelector.getActiveImageData();
        const activeLabel: LabelPolygon = LabelsSelector.getActivePolygonLabel();
        const newAnchorPositionOnImage: IPoint =
            RenderEngineUtil.transferPointFromViewPortContentToImage(this.suggestedAnchorPositionOnCanvas, data);
        const insert = (arr, index, newItem) => [...arr.slice(0, index), newItem, ...arr.slice(index)];

        const newImageData: ImageData = {
            ...imageData,
            labelPolygons: imageData.labelPolygons.map((polygon: LabelPolygon) => {
                if (polygon.id !== activeLabel.id) {
                    return polygon
                } else {
                    return {
                        ...polygon,
                        vertices: insert(polygon.vertices, this.suggestedAnchorIndexInPolygon, newAnchorPositionOnImage)
                    }
                }
            })
        };

        store.dispatch(updateImageDataById(newImageData.image_id, newImageData));
        this.startExistingLabelResize(data, activeLabel.id, this.suggestedAnchorIndexInPolygon);
        this.discardSuggestedPoint();
    }

    // =================================================================================================================
    // VALIDATORS
    // =================================================================================================================

    public isInProgress(): boolean {
        return this.isCreationInProgress() || this.isResizeInProgress();
    }
    
    private isCreationInProgress(): boolean {
        return this.activePath !== null && this.activePath.length !== 0;
    }
    
    private isResizeInProgress(): boolean {
        return this.resizeAnchorIndex !== null;
    }

    private isMouseOverAnchor(mouse: IPoint, anchor: IPoint): boolean {
        if (!mouse || !anchor) return null;
        return RectUtil.isPointInside(RectUtil.getRectWithCenterAndSize(anchor, RenderEngineSettings.anchorSize), mouse);
    }

    // =================================================================================================================
    // MAPPERS
    // =================================================================================================================

    private mapPointsToLines(points: IPoint[]): ILine[] {
        const lines: ILine[] = [];
        for (let i = 0; i < points.length - 1; i++) {
            lines.push({start: points[i], end: points[i + 1]})
        }
        return lines;
    }

    // =================================================================================================================
    // GETTERS
    // =================================================================================================================

    private getPolygonUnderMouse(data: EditorData): LabelPolygon {
        const labelPolygons: LabelPolygon[] = LabelsSelector.getActiveImageData().labelPolygons;
        for (let i = 0; i < labelPolygons.length; i++) {
            const pathOnCanvas: IPoint[] = RenderEngineUtil.transferPolygonFromImageToViewPortContent(labelPolygons[i].vertices, data);
            const linesOnCanvas: ILine[] = this.mapPointsToLines(pathOnCanvas.concat(pathOnCanvas[0]));

            for (let j = 0; j < linesOnCanvas.length; j++) {
                const mouseOverLine = RenderEngineUtil.isMouseOverLine(
                    data.mousePositionOnViewPortContent,
                    linesOnCanvas[j],
                    RenderEngineSettings.anchorHoverSize.width / 2
                )
                if (mouseOverLine)
                    return labelPolygons[i];
            }
            for (let j = 0; j < pathOnCanvas.length; j ++) {
                if (this.isMouseOverAnchor(data.mousePositionOnViewPortContent, pathOnCanvas[j]))
                    return labelPolygons[i];
            }
        }
        return null;
    }

    private getAnchorUnderMouse(data: EditorData): IPoint {
        const labelPolygons: LabelPolygon[] = LabelsSelector.getActiveImageData().labelPolygons;
        for (let i = 0; i < labelPolygons.length; i++) {
            const pathOnCanvas: IPoint[] = RenderEngineUtil.transferPolygonFromImageToViewPortContent(labelPolygons[i].vertices, data);
            for (let j = 0; j < pathOnCanvas.length; j ++) {
                if (this.isMouseOverAnchor(data.mousePositionOnViewPortContent, pathOnCanvas[j]))
                    return pathOnCanvas[j];
            }
        }
        return null;
    }
}
